import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent as ReactKeyboardEvent,
  type MouseEvent as ReactMouseEvent,
} from 'react'
import CalendarTodayRoundedIcon from '@mui/icons-material/CalendarTodayRounded'
import KeyboardArrowLeftRoundedIcon from '@mui/icons-material/KeyboardArrowLeftRounded'
import KeyboardArrowRightRoundedIcon from '@mui/icons-material/KeyboardArrowRightRounded'
import KeyboardDoubleArrowLeftRoundedIcon from '@mui/icons-material/KeyboardDoubleArrowLeftRounded'
import KeyboardDoubleArrowRightRoundedIcon from '@mui/icons-material/KeyboardDoubleArrowRightRounded'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import { FloatingMenu, type FloatingMenuPlacement } from './FloatingMenu'
import { OverflowTip } from './Hover/OverflowTip'
import { Button } from './Button'
import { Pressable } from './Pressable'
import './datePicker.css'

/* ────────────────────────────────────────────────────────────────────────
 * Date helpers — YYYY-MM-DD strings are the canonical wire format (matches
 * <input type="date">), Date objects are used only internally at local
 * midnight so timezone drift never shifts the day.
 * ──────────────────────────────────────────────────────────────────────── */

import { parseIsoDate, toIsoDate } from './datePickerHelpers'

const pad2 = (n: number) => (n < 10 ? `0${n}` : String(n))

const sameDay = (a: Date, b: Date): boolean =>
  a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate()

const startOfMonth = (d: Date): Date => new Date(d.getFullYear(), d.getMonth(), 1)
const addMonths = (d: Date, delta: number): Date =>
  new Date(d.getFullYear(), d.getMonth() + delta, 1)
const addYears = (d: Date, delta: number): Date =>
  new Date(d.getFullYear() + delta, d.getMonth(), 1)
const addDays = (d: Date, delta: number): Date =>
  new Date(d.getFullYear(), d.getMonth(), d.getDate() + delta)

/** Monday of the ISO week containing `d`, at local midnight. */
const startOfIsoWeek = (d: Date): Date => {
  const clone = new Date(d.getFullYear(), d.getMonth(), d.getDate())
  const day = clone.getDay() // 0=Sun..6=Sat
  const delta = (day + 6) % 7 // Mon=0
  clone.setDate(clone.getDate() - delta)
  return clone
}

/** Sunday of the ISO week containing `d`, at local midnight. */
const endOfIsoWeek = (d: Date): Date => addDays(startOfIsoWeek(d), 6)

const clampToBounds = (d: Date, min: Date | null, max: Date | null): Date => {
  if (min && d < min) return new Date(min)
  if (max && d > max) return new Date(max)
  return d
}

const isDisabled = (d: Date, min: Date | null, max: Date | null): boolean => {
  if (min && d < min) return true
  if (max && d > max) return true
  return false
}

/** Days visible in a 6-row month grid starting on Sunday. */
const monthMatrix = (monthStart: Date): Date[] => {
  const first = new Date(monthStart)
  const firstDow = first.getDay()
  const gridStart = addDays(first, -firstDow)
  const cells: Date[] = []
  for (let i = 0; i < 42; i += 1) cells.push(addDays(gridStart, i))
  return cells
}

const WEEKDAY_LABELS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'] as const
const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
] as const
const MONTH_NAMES_SHORT = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
] as const

/** DD/MM/YYYY — matches the format already shown in the app's paginator. */
const formatDisplayShort = (d: Date): string =>
  `${pad2(d.getDate())}/${pad2(d.getMonth() + 1)}/${d.getFullYear()}`

const formatDisplayLong = (d: Date): string =>
  `${MONTH_NAMES_SHORT[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`

/* ────────────────────────────────────────────────────────────────────────
 * Shared popover — month grid, header nav, year/month pickers.
 * ──────────────────────────────────────────────────────────────────────── */

type PopoverMode = 'day' | 'month' | 'year'

type MonthPanelProps = {
  monthStart: Date
  selectedStart: Date | null
  selectedEnd: Date | null
  hoverDate: Date | null
  min: Date | null
  max: Date | null
  onPickDay: (d: Date) => void
  onHoverDay: (d: Date | null) => void
  onShiftMonth: (delta: number) => void
  onShiftYear: (delta: number) => void
  onOpenMonthPicker: () => void
  onOpenYearPicker: () => void
  showBoundaryArrows: 'both' | 'left' | 'right' | 'none'
  today: Date
  ariaLabelPrev: string
  ariaLabelNext: string
  /** When 'week', selection / hover / today highlights extend across the full
   * ISO week (Mon–Sun) containing the anchor day, and today's anchor is the
   * week's Monday. Purely a rendering hint — click handling is still per-day
   * and callers snap in `onPickDay`. */
  selectionMode?: 'day' | 'week'
}

const MonthPanel = ({
  monthStart,
  selectedStart,
  selectedEnd,
  hoverDate,
  min,
  max,
  onPickDay,
  onHoverDay,
  onShiftMonth,
  onShiftYear,
  onOpenMonthPicker,
  onOpenYearPicker,
  showBoundaryArrows,
  today,
  ariaLabelPrev,
  ariaLabelNext,
  selectionMode = 'day',
}: MonthPanelProps) => {
  const cells = useMemo(() => monthMatrix(monthStart), [monthStart])
  const monthIdx = monthStart.getMonth()
  const year = monthStart.getFullYear()

  // In week mode a single "selected" anchor lights up the whole ISO week it
  // belongs to, so treat that as a committed range [Mon, Sun]. Hover does the
  // same but as a preview overlay (no commit until click).
  const weekMode = selectionMode === 'week'
  const rangeLow = weekMode && selectedStart
    ? startOfIsoWeek(selectedStart)
    : (selectedStart && selectedEnd
      ? (selectedStart <= selectedEnd ? selectedStart : selectedEnd)
      : selectedStart)
  const rangeHigh = weekMode && selectedStart
    ? endOfIsoWeek(selectedStart)
    : (selectedStart && selectedEnd
      ? (selectedStart <= selectedEnd ? selectedEnd : selectedStart)
      : null)

  const previewLow = weekMode && hoverDate
    ? startOfIsoWeek(hoverDate)
    : (selectedStart && !selectedEnd && hoverDate
      ? (selectedStart <= hoverDate ? selectedStart : hoverDate)
      : null)
  const previewHigh = weekMode && hoverDate
    ? endOfIsoWeek(hoverDate)
    : (selectedStart && !selectedEnd && hoverDate
      ? (selectedStart <= hoverDate ? hoverDate : selectedStart)
      : null)

  const showLeft = showBoundaryArrows === 'both' || showBoundaryArrows === 'left'
  const showRight = showBoundaryArrows === 'both' || showBoundaryArrows === 'right'

  return (
    <div className="dsDatePicker__month">
      <div className="dsDatePicker__header">
        {showLeft ? (
          <>
            <Pressable
              className="dsDatePicker__navBtn"
              onClick={() => onShiftYear(-1)}
              aria-label="Previous year"
            >
              <KeyboardDoubleArrowLeftRoundedIcon fontSize="inherit" />
            </Pressable>
            <Pressable
              className="dsDatePicker__navBtn"
              onClick={() => onShiftMonth(-1)}
              aria-label={ariaLabelPrev}
            >
              <KeyboardArrowLeftRoundedIcon fontSize="inherit" />
            </Pressable>
          </>
        ) : (
          <span className="dsDatePicker__navBtn" aria-hidden="true" style={{ visibility: 'hidden' }} />
        )}

        <span className="dsDatePicker__title">
          <Pressable
            className="dsDatePicker__titleBtn"
            onClick={onOpenMonthPicker}
            aria-label={`Choose month, currently ${MONTH_NAMES[monthIdx]}`}
          >
            {MONTH_NAMES[monthIdx]}
          </Pressable>
          <Pressable
            className="dsDatePicker__titleBtn"
            onClick={onOpenYearPicker}
            aria-label={`Choose year, currently ${year}`}
          >
            {year}
          </Pressable>
        </span>

        {showRight ? (
          <>
            <Pressable
              className="dsDatePicker__navBtn"
              onClick={() => onShiftMonth(1)}
              aria-label={ariaLabelNext}
            >
              <KeyboardArrowRightRoundedIcon fontSize="inherit" />
            </Pressable>
            <Pressable
              className="dsDatePicker__navBtn"
              onClick={() => onShiftYear(1)}
              aria-label="Next year"
            >
              <KeyboardDoubleArrowRightRoundedIcon fontSize="inherit" />
            </Pressable>
          </>
        ) : (
          <span className="dsDatePicker__navBtn" aria-hidden="true" style={{ visibility: 'hidden' }} />
        )}
      </div>

      <div className="dsDatePicker__grid" role="grid">
        {WEEKDAY_LABELS.map((w, i) => (
          <div key={`w${i}`} className="dsDatePicker__weekday" role="columnheader">
            {w}
          </div>
        ))}
        {cells.map((d) => {
          const outside = d.getMonth() !== monthIdx
          const disabled = isDisabled(d, min, max)
          // In week mode there is no single "selected" cell — the whole ISO
          // week highlights via rangeLow / rangeHigh below.
          const isSelected = weekMode
            ? false
            : ((selectedStart && sameDay(d, selectedStart))
              || (selectedEnd && sameDay(d, selectedEnd)))
          const isRangeStart = rangeLow && sameDay(d, rangeLow)
          const isRangeEnd = rangeHigh && sameDay(d, rangeHigh)
          const inCommitted = rangeLow && rangeHigh && d > rangeLow && d < rangeHigh
          const inPreview = previewLow && previewHigh
            && d >= previewLow && d <= previewHigh
            && !(!weekMode && selectedStart && sameDay(d, selectedStart))
          const inRange = inCommitted || inPreview
          const isToday = sameDay(d, today)
          const classes = [
            'dsDatePicker__day',
            outside ? 'is-outside' : '',
            isToday ? 'is-today' : '',
            isSelected ? 'is-selected' : '',
            isRangeStart ? 'is-range-start' : '',
            isRangeEnd ? 'is-range-end' : '',
            inRange ? 'is-in-range' : '',
          ].filter(Boolean).join(' ')
          return (
            <Pressable
              key={toIsoDate(d)}
              className={classes}
              disabled={disabled}
              onClick={() => onPickDay(d)}
              onMouseEnter={() => onHoverDay(d)}
              onFocus={() => onHoverDay(d)}
              tabIndex={outside ? -1 : 0}
              aria-label={`${d.getDate()} ${MONTH_NAMES[d.getMonth()]} ${d.getFullYear()}`}
              aria-current={isToday ? 'date' : undefined}
              aria-selected={isSelected ? true : undefined}
              data-iso={toIsoDate(d)}
            >
              {d.getDate()}
            </Pressable>
          )
        })}
      </div>
    </div>
  )
}

type MonthPickerListProps = {
  activeMonth: number
  min: Date | null
  max: Date | null
  year: number
  onPick: (m: number) => void
}
const MonthPickerList = ({ activeMonth, min, max, year, onPick }: MonthPickerListProps) => (
  <div className="dsDatePicker__pickerGrid">
    {MONTH_NAMES_SHORT.map((label, idx) => {
      const monthLast = new Date(year, idx + 1, 0)
      const monthFirst = new Date(year, idx, 1)
      const disabled = (min && monthLast < startOfMonth(min))
        || (max && monthFirst > max)
      return (
        <Pressable
          key={label}
          className={`dsDatePicker__pickerCell${idx === activeMonth ? ' is-selected' : ''}`}
          disabled={Boolean(disabled)}
          onClick={() => onPick(idx)}
        >
          {label}
        </Pressable>
      )
    })}
  </div>
)

type YearPickerListProps = {
  activeYear: number
  min: Date | null
  max: Date | null
  onPick: (y: number) => void
}
const YearPickerList = ({ activeYear, min, max, onPick }: YearPickerListProps) => {
  const lowerBound = min ? min.getFullYear() : activeYear - 60
  const upperBound = max ? max.getFullYear() : activeYear + 30
  const start = Math.min(lowerBound, activeYear - 8)
  const end = Math.max(upperBound, activeYear + 8)
  const years: number[] = []
  for (let y = start; y <= end; y += 1) years.push(y)
  const ref = useRef<HTMLDivElement | null>(null)
  useEffect(() => {
    if (!ref.current) return
    const el = ref.current.querySelector<HTMLButtonElement>(`[data-year="${activeYear}"]`)
    if (el) el.scrollIntoView({ block: 'center' })
  }, [activeYear])
  return (
    <div className="dsDatePicker__yearList" ref={ref}>
      <div className="dsDatePicker__pickerGrid">
        {years.map((y) => {
          const disabled = (min && y < min.getFullYear()) || (max && y > max.getFullYear())
          return (
            <Pressable
              key={y}
              data-year={y}
              className={`dsDatePicker__pickerCell${y === activeYear ? ' is-selected' : ''}`}
              disabled={Boolean(disabled)}
              onClick={() => onPick(y)}
            >
              {y}
            </Pressable>
          )
        })}
      </div>
    </div>
  )
}

/* ────────────────────────────────────────────────────────────────────────
 * DatePicker — single-date, drop-in replacement for <input type="date">.
 * ──────────────────────────────────────────────────────────────────────── */

export type DatePickerProps = {
  /** ISO YYYY-MM-DD string. Empty string / undefined = no selection.
   *  In `selectionMode='week'`, any day inside the target ISO week is
   *  accepted — the picker highlights the full Mon–Sun containing it. */
  value: string | null | undefined
  /** Emits ISO YYYY-MM-DD string. Empty string when cleared.
   *  In `selectionMode='week'`, emits the ISO date of the Monday of the
   *  week the user clicked into. */
  onChange: (value: string) => void
  min?: string
  max?: string
  disabled?: boolean
  readOnly?: boolean
  placeholder?: string
  ariaLabel?: string
  className?: string
  id?: string
  name?: string
  size?: 'sm' | 'md'
  /** Show a small × chip inside the trigger when a value is present. Default true. */
  clearable?: boolean
  /** Display formatter. Defaults to 'DD/MM/YYYY'. In `selectionMode='week'`
   *  the default formatter shows the week range "Mon dd – Sun dd, yyyy"; pass
   *  a function to override. */
  format?: 'short' | 'long' | ((d: Date) => string)
  placement?: FloatingMenuPlacement
  style?: CSSProperties
  /** Optional footer copy override for the "Today" button. In week mode
   *  the button always jumps to (and highlights) the current week. */
  todayLabel?: string
  /** Called when the popover closes. Useful for onBlur-like semantics. */
  onClose?: () => void
  /** 'day' (default) picks a single date. 'week' snaps any click to the
   *  Monday of the ISO week containing the clicked day and highlights the
   *  full Mon–Sun span on hover and after commit. */
  selectionMode?: 'day' | 'week'
}

const formatValue = (
  d: Date,
  fmt: DatePickerProps['format'],
): string => {
  if (typeof fmt === 'function') return fmt(d)
  if (fmt === 'long') return formatDisplayLong(d)
  return formatDisplayShort(d)
}

const formatWeekRange = (d: Date): string => {
  const mon = startOfIsoWeek(d)
  const sun = endOfIsoWeek(d)
  const sameYear = mon.getFullYear() === sun.getFullYear()
  const sameMonth = sameYear && mon.getMonth() === sun.getMonth()
  const left = sameMonth
    ? `${MONTH_NAMES_SHORT[mon.getMonth()]} ${mon.getDate()}`
    : `${MONTH_NAMES_SHORT[mon.getMonth()]} ${mon.getDate()}`
  const right = sameYear
    ? (sameMonth
      ? `${sun.getDate()}, ${sun.getFullYear()}`
      : `${MONTH_NAMES_SHORT[sun.getMonth()]} ${sun.getDate()}, ${sun.getFullYear()}`)
    : `${MONTH_NAMES_SHORT[sun.getMonth()]} ${sun.getDate()}, ${sun.getFullYear()}`
  return `${left} – ${right}`
}

export const DatePicker = ({
  value,
  onChange,
  min,
  max,
  disabled = false,
  readOnly = false,
  placeholder = 'Select date',
  ariaLabel,
  className,
  id,
  name,
  size = 'md',
  clearable = true,
  format = 'short',
  placement = 'bottom-start',
  style,
  todayLabel = 'Today',
  onClose,
  selectionMode = 'day',
}: DatePickerProps) => {
  const anchorRef = useRef<HTMLButtonElement | null>(null)
  const [open, setOpen] = useState(false)
  const [mode, setMode] = useState<PopoverMode>('day')
  const [hover, setHover] = useState<Date | null>(null)

  const weekMode = selectionMode === 'week'

  const minDate = useMemo(() => parseIsoDate(min ?? null), [min])
  const maxDate = useMemo(() => parseIsoDate(max ?? null), [max])
  const selectedDate = useMemo(() => parseIsoDate(value ?? null), [value])
  const today = useMemo(() => {
    const t = new Date()
    return new Date(t.getFullYear(), t.getMonth(), t.getDate())
  }, [])

  const [viewMonth, setViewMonth] = useState<Date>(() =>
    startOfMonth(selectedDate ?? clampToBounds(today, minDate, maxDate)))

  const [prevOpen, setPrevOpen] = useState(open)
  if (prevOpen !== open) {
    setPrevOpen(open)
    if (open) {
      setViewMonth(startOfMonth(selectedDate ?? clampToBounds(today, minDate, maxDate)))
      setMode('day')
      setHover(null)
    }
  }

  const handleClose = useCallback(() => {
    setOpen(false)
    setHover(null)
    onClose?.()
  }, [onClose])

  const handleToggle = useCallback(() => {
    if (disabled || readOnly) return
    setOpen((prev) => !prev)
  }, [disabled, readOnly])

  const handlePickDay = useCallback((d: Date) => {
    // In week mode any clicked day snaps to the Monday of its ISO week
    // before we emit — that's the caller's committed value.
    const emitted = weekMode ? startOfIsoWeek(d) : d
    onChange(toIsoDate(emitted))
    handleClose()
  }, [onChange, handleClose, weekMode])

  const handleClear = useCallback((event: ReactMouseEvent) => {
    event.stopPropagation()
    if (disabled || readOnly) return
    onChange('')
  }, [disabled, readOnly, onChange])

  const handleToday = useCallback(() => {
    if (isDisabled(today, minDate, maxDate)) return
    handlePickDay(today)
  }, [today, minDate, maxDate, handlePickDay])

  const handleTriggerKey = useCallback((event: ReactKeyboardEvent<HTMLButtonElement>) => {
    if (disabled || readOnly) return
    if (event.key === 'ArrowDown' || event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      setOpen(true)
    }
  }, [disabled, readOnly])

  // Whether the Today button in the popover footer has anything to do.
  // Disabled when the current value is already the same day (or, in week
  // mode, the same ISO week) as today — matches the paginator-level Today
  // chip semantics so users know "click me to jump" only lights up when
  // there IS a jump to make.
  const isAlreadyOnToday = selectedDate
    ? (weekMode
      ? startOfIsoWeek(selectedDate).getTime() === startOfIsoWeek(today).getTime()
      : sameDay(selectedDate, today))
    : false

  const triggerLabel = selectedDate
    ? (weekMode && format === 'short'
      ? formatWeekRange(selectedDate)
      : formatValue(selectedDate, format))
    : placeholder
  const showClear = clearable && selectedDate && !disabled && !readOnly

  return (
    <>
      <Pressable
        ref={anchorRef}
        id={id}
        name={name}
        className={[
          'dsDatePicker__trigger',
          size === 'sm' ? 'dsDatePicker__trigger--sm' : '',
          selectedDate ? '' : 'dsDatePicker__trigger--empty',
          open ? 'is-open' : '',
          className ?? '',
        ].filter(Boolean).join(' ')}
        style={style}
        disabled={disabled}
        aria-disabled={disabled || undefined}
        aria-label={ariaLabel ?? placeholder}
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={handleToggle}
        onKeyDown={handleTriggerKey}
      >
        <span className="dsDatePicker__triggerIcon" aria-hidden="true">
          <CalendarTodayRoundedIcon fontSize="inherit" />
        </span>
        {typeof triggerLabel === 'string' ? (
          <OverflowTip label={triggerLabel}>
            <span className="dsDatePicker__triggerLabel">{triggerLabel}</span>
          </OverflowTip>
        ) : (
          <span className="dsDatePicker__triggerLabel">{triggerLabel}</span>
        )}
        {showClear ? (
          <span
            role="button"
            tabIndex={-1}
            className="dsDatePicker__triggerClear"
            onClick={handleClear}
            aria-label="Clear date"
          >
            <CloseRoundedIcon fontSize="inherit" />
          </span>
        ) : null}
      </Pressable>

      <FloatingMenu
        anchorRef={anchorRef}
        isOpen={open}
        onClose={handleClose}
        placement={placement}
        className="dsDatePicker__popover"
        role="dialog"
        ariaLabel={ariaLabel ?? 'Choose a date'}
      >
        {mode === 'day' && (
          <MonthPanel
            monthStart={viewMonth}
            selectedStart={selectedDate}
            selectedEnd={null}
            hoverDate={weekMode ? hover : null}
            min={minDate}
            max={maxDate}
            onPickDay={handlePickDay}
            onHoverDay={weekMode ? setHover : () => undefined}
            onShiftMonth={(delta) => setViewMonth((m) => addMonths(m, delta))}
            onShiftYear={(delta) => setViewMonth((m) => addYears(m, delta))}
            onOpenMonthPicker={() => setMode('month')}
            onOpenYearPicker={() => setMode('year')}
            showBoundaryArrows="both"
            today={today}
            ariaLabelPrev="Previous month"
            ariaLabelNext="Next month"
            selectionMode={selectionMode}
          />
        )}
        {mode === 'month' && (
          <MonthPickerList
            activeMonth={viewMonth.getMonth()}
            year={viewMonth.getFullYear()}
            min={minDate}
            max={maxDate}
            onPick={(m) => {
              setViewMonth(new Date(viewMonth.getFullYear(), m, 1))
              setMode('day')
            }}
          />
        )}
        {mode === 'year' && (
          <YearPickerList
            activeYear={viewMonth.getFullYear()}
            min={minDate}
            max={maxDate}
            onPick={(y) => {
              setViewMonth(new Date(y, viewMonth.getMonth(), 1))
              setMode('month')
            }}
          />
        )}
        <div className="dsDatePicker__footer">
          <Button
            variant="tertiary"
            size="sm"
            onClick={() => onChange('')}
          >
            Clear
          </Button>
          <Button
            variant="secondary"
            size="sm"
            onClick={handleToday}
            disabled={isDisabled(today, minDate, maxDate) || isAlreadyOnToday}
            title={isAlreadyOnToday
              ? (weekMode ? 'Already on this week' : 'Already on today')
              : undefined}
          >
            {todayLabel}
          </Button>
        </div>
      </FloatingMenu>
    </>
  )
}

/* ────────────────────────────────────────────────────────────────────────
 * DateRangePicker — dual-month view with hover-range preview.
 * ──────────────────────────────────────────────────────────────────────── */

export type DateRangeValue = { start: string; end: string }

export type DateRangePickerProps = {
  startValue: string | null | undefined
  endValue: string | null | undefined
  onChange: (range: DateRangeValue) => void
  min?: string
  max?: string
  disabled?: boolean
  readOnly?: boolean
  placeholder?: string
  ariaLabel?: string
  className?: string
  size?: 'sm' | 'md'
  format?: 'short' | 'long' | ((d: Date) => string)
  placement?: FloatingMenuPlacement
  style?: CSSProperties
  clearable?: boolean
}

export const DateRangePicker = ({
  startValue,
  endValue,
  onChange,
  min,
  max,
  disabled = false,
  readOnly = false,
  placeholder = 'Select range',
  ariaLabel,
  className,
  size = 'md',
  format = 'short',
  placement = 'bottom-start',
  style,
  clearable = true,
}: DateRangePickerProps) => {
  const anchorRef = useRef<HTMLButtonElement | null>(null)
  const [open, setOpen] = useState(false)
  const [mode, setMode] = useState<PopoverMode>('day')

  const minDate = useMemo(() => parseIsoDate(min ?? null), [min])
  const maxDate = useMemo(() => parseIsoDate(max ?? null), [max])
  const startDate = useMemo(() => parseIsoDate(startValue ?? null), [startValue])
  const endDate = useMemo(() => parseIsoDate(endValue ?? null), [endValue])
  const today = useMemo(() => {
    const t = new Date()
    return new Date(t.getFullYear(), t.getMonth(), t.getDate())
  }, [])

  const [viewMonth, setViewMonth] = useState<Date>(() =>
    startOfMonth(startDate ?? clampToBounds(today, minDate, maxDate)))
  const [pending, setPending] = useState<Date | null>(null)
  const [hover, setHover] = useState<Date | null>(null)

  const [prevOpen, setPrevOpen] = useState(open)
  if (prevOpen !== open) {
    setPrevOpen(open)
    if (open) {
      setViewMonth(startOfMonth(startDate ?? clampToBounds(today, minDate, maxDate)))
      setPending(null)
      setHover(null)
      setMode('day')
    }
  }

  const handleClose = useCallback(() => {
    setOpen(false)
    setPending(null)
    setHover(null)
  }, [])

  const handleToggle = useCallback(() => {
    if (disabled || readOnly) return
    setOpen((prev) => !prev)
  }, [disabled, readOnly])

  const handlePickDay = useCallback((d: Date) => {
    if (!pending) {
      setPending(d)
      setHover(d)
      return
    }
    const start = pending <= d ? pending : d
    const end = pending <= d ? d : pending
    onChange({ start: toIsoDate(start), end: toIsoDate(end) })
    setPending(null)
    setHover(null)
    setOpen(false)
  }, [pending, onChange])

  const handleClear = useCallback((event: ReactMouseEvent) => {
    event.stopPropagation()
    if (disabled || readOnly) return
    onChange({ start: '', end: '' })
  }, [disabled, readOnly, onChange])

  const secondMonth = useMemo(() => addMonths(viewMonth, 1), [viewMonth])

  const triggerLabel = (() => {
    if (startDate && endDate) return `${formatValue(startDate, format)}  →  ${formatValue(endDate, format)}`
    if (startDate) return `${formatValue(startDate, format)} → …`
    return placeholder
  })()
  const showClear = clearable && (startDate || endDate) && !disabled && !readOnly

  const effectiveStart = pending ?? startDate
  const effectiveEnd = pending ? null : endDate

  return (
    <>
      <Pressable
        ref={anchorRef}
        className={[
          'dsDatePicker__trigger',
          size === 'sm' ? 'dsDatePicker__trigger--sm' : '',
          (startDate || endDate) ? '' : 'dsDatePicker__trigger--empty',
          open ? 'is-open' : '',
          className ?? '',
        ].filter(Boolean).join(' ')}
        style={style}
        disabled={disabled}
        aria-disabled={disabled || undefined}
        aria-label={ariaLabel ?? placeholder}
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={handleToggle}
      >
        <span className="dsDatePicker__triggerIcon" aria-hidden="true">
          <CalendarTodayRoundedIcon fontSize="inherit" />
        </span>
        {typeof triggerLabel === 'string' ? (
          <OverflowTip label={triggerLabel}>
            <span className="dsDatePicker__triggerLabel">{triggerLabel}</span>
          </OverflowTip>
        ) : (
          <span className="dsDatePicker__triggerLabel">{triggerLabel}</span>
        )}
        {showClear ? (
          <span
            role="button"
            tabIndex={-1}
            className="dsDatePicker__triggerClear"
            onClick={handleClear}
            aria-label="Clear range"
          >
            <CloseRoundedIcon fontSize="inherit" />
          </span>
        ) : null}
      </Pressable>

      <FloatingMenu
        anchorRef={anchorRef}
        isOpen={open}
        onClose={handleClose}
        placement={placement}
        className="dsDatePicker__popover dsDatePicker__popover--range"
        role="dialog"
        ariaLabel={ariaLabel ?? 'Choose a date range'}
      >
        {mode === 'day' && (
          <div className="dsDatePicker__monthRow">
            <MonthPanel
              monthStart={viewMonth}
              selectedStart={effectiveStart}
              selectedEnd={effectiveEnd}
              hoverDate={hover}
              min={minDate}
              max={maxDate}
              onPickDay={handlePickDay}
              onHoverDay={setHover}
              onShiftMonth={(delta) => setViewMonth((m) => addMonths(m, delta))}
              onShiftYear={(delta) => setViewMonth((m) => addYears(m, delta))}
              onOpenMonthPicker={() => setMode('month')}
              onOpenYearPicker={() => setMode('year')}
              showBoundaryArrows="left"
              today={today}
              ariaLabelPrev="Previous month"
              ariaLabelNext="Next month"
            />
            <MonthPanel
              monthStart={secondMonth}
              selectedStart={effectiveStart}
              selectedEnd={effectiveEnd}
              hoverDate={hover}
              min={minDate}
              max={maxDate}
              onPickDay={handlePickDay}
              onHoverDay={setHover}
              onShiftMonth={(delta) => setViewMonth((m) => addMonths(m, delta))}
              onShiftYear={(delta) => setViewMonth((m) => addYears(m, delta))}
              onOpenMonthPicker={() => setMode('month')}
              onOpenYearPicker={() => setMode('year')}
              showBoundaryArrows="right"
              today={today}
              ariaLabelPrev="Previous month"
              ariaLabelNext="Next month"
            />
          </div>
        )}
        {mode === 'month' && (
          <MonthPickerList
            activeMonth={viewMonth.getMonth()}
            year={viewMonth.getFullYear()}
            min={minDate}
            max={maxDate}
            onPick={(m) => {
              setViewMonth(new Date(viewMonth.getFullYear(), m, 1))
              setMode('day')
            }}
          />
        )}
        {mode === 'year' && (
          <YearPickerList
            activeYear={viewMonth.getFullYear()}
            min={minDate}
            max={maxDate}
            onPick={(y) => {
              setViewMonth(new Date(y, viewMonth.getMonth(), 1))
              setMode('month')
            }}
          />
        )}
        <div className="dsDatePicker__footer">
          <Button
            variant="tertiary"
            size="sm"
            onClick={() => onChange({ start: '', end: '' })}
          >
            Clear
          </Button>
          {(() => {
            const alreadyOnToday = Boolean(
              startDate && endDate && sameDay(startDate, today) && sameDay(endDate, today),
            )
            return (
              <Button
                variant="secondary"
                size="sm"
                onClick={() => {
                  const t = today
                  if (isDisabled(t, minDate, maxDate) || alreadyOnToday) return
                  onChange({ start: toIsoDate(t), end: toIsoDate(t) })
                  setOpen(false)
                }}
                disabled={isDisabled(today, minDate, maxDate) || alreadyOnToday}
                title={alreadyOnToday ? 'Already on today' : undefined}
              >
                Today
              </Button>
            )
          })()}
        </div>
      </FloatingMenu>
    </>
  )
}
